"use client"

import { Search, PenTool, Code, Rocket } from "lucide-react"
import { useInView } from "@/hooks/use-in-view"
import { cn } from "@/lib/utils"

const steps = [
  {
    number: "01",
    icon: Search,
    title: "Análisis y Descubrimiento",
    description:
      "Realizamos un análisis exhaustivo de tus procesos actuales. Identificamos ineficiencias, cuellos de botella y oportunidades de mejora.",
    details: [],
  },
  {
    number: "02",
    icon: PenTool,
    title: "Diseño de Solución",
    description:
      "Creamos una propuesta personalizada que aborda tus necesidades específicas. Priorizamos funcionalidades según el impacto y viabilidad.",
    details: [],
  },
  {
    number: "03",
    icon: Code,
    title: "Desarrollo Ágil",
    description:
      "Desarrollamos tu aplicación web portátil con metodologías ágiles. Entregas incrementales para que veas el progreso en tiempo real.",
    details: [],
  },
  {
    number: "04",
    icon: Rocket,
    title: "Despliegue y Capacitación",
    description:
      "Implementamos la solución en tu entorno y capacitamos a tu equipo. Aseguramos una transición suave y sin interrupciones.",
    details: [],
  },
]

export function Process() {
  const { ref: titleRef, isInView: titleInView } =
    useInView<HTMLHeadingElement>()

  return (
    <section id="proceso" className="py-16 lg:py-24 bg-background-process">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            ref={titleRef}
            className={cn(
              "mt-4 font-work text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-titulo-hero transition-all duration-1000 ease-out will-change-transform",
              "motion-reduce:transition-none motion-reduce:transform-none",
              titleInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3",
            )}
          >
            De la idea a la solución
          </h2>
          <p
            className={cn(
              "mt-3 font-jetbrains font-extralight text-lg text-titulo-hero/60 transition-all duration-1000 ease-out will-change-transform",
              "motion-reduce:transition-none motion-reduce:transform-none",
              titleInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-3",
            )}
          >
            Un proceso transparente y colaborativo que garantiza resultados excepcionales.
          </p>
        </div>

        {/* Process steps */}
        <div className="mt-20 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-8">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector line for desktop */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-[calc(25%+3rem)] w-[calc(70%-3rem)] h-px bg-icon-hover-red/50" />
              )}

              <div className="relative flex flex-col items-center text-center lg:items-start lg:text-left">
                {/* Step number */}
                <div className="flex items-center justify-center">
                  <span className="font-work text-6xl lg:text-7xl font-bold text-titulo-red/50">
                    {step.number}
                  </span>
                </div>

                {/* Icon */}
                <div className="mt-4 flex items-center justify-center">
                  <step.icon className="h-10 w-10 text-titulo-hero/50" stroke-width="0.8"/>
                </div>

                {/* Content */}
                <h3 className="mt-6 font-work text-xl font-semibold text-titulo-hero/80">
                  {step.title}
                </h3>
                <p className="font-jetbrains text-base font-thin mt-3 text-titulo-hero/60 leading-relaxed">
                  {step.description}
                </p>

              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
